import { useState, useRef, useCallback } from 'react';
import { usePresignedUrl } from '@/api/evidence';

export type FileValue = {
  url: string;
  name: string;
  size: number;
  type: string;
};

export type UploadState = 'idle' | 'uploading' | 'uploaded' | 'error';

type UseFileUploadArgs = {
  folder: 'documents' | 'evidence' | 'certificates' | 'thumbnails' | 'avatars';
  accept?: string;
  maxSizeMB: number;
  value?: FileValue | null;
  onChange: (file: FileValue | null) => void;
  disabled: boolean;
};

// Encapsula el estado + validación + subida por presigned URL (XHR con progreso).
// Extraído de file-upload.tsx por ENG-LOC-01.
export function useFileUpload({
  folder,
  accept,
  maxSizeMB,
  value,
  onChange,
  disabled,
}: UseFileUploadArgs) {
  const [state, setState] = useState<UploadState>(value?.url ? 'uploaded' : 'idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const presignedMut = usePresignedUrl();

  const maxBytes = maxSizeMB * 1024 * 1024;
  const acceptedExtensions = accept
    ? accept.split(',').map((ext) => ext.trim().toLowerCase())
    : null;

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxBytes) {
        return `El archivo excede el límite de ${maxSizeMB}MB`;
      }
      if (acceptedExtensions) {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        const mimeMatch = acceptedExtensions.some(
          (a) => a === ext || file.type === a || file.type.startsWith(a.replace('*', '')),
        );
        if (!mimeMatch) {
          return `Tipo de archivo no permitido. Aceptados: ${accept}`;
        }
      }
      return null;
    },
    [maxBytes, maxSizeMB, accept, acceptedExtensions],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setErrorMsg(validationError);
        setState('error');
        return;
      }

      setState('uploading');
      setErrorMsg('');
      setProgress(10);

      try {
        const res = await presignedMut.mutateAsync({
          folder,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
        });
        const presigned = res.data;

        setProgress(30);

        const xhr = new XMLHttpRequest();

        await new Promise<void>((resolve, reject) => {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const pct = 30 + Math.round((e.loaded / e.total) * 60);
              setProgress(pct);
            }
          });
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Upload failed: ${xhr.status}`));
          });
          xhr.addEventListener('error', () => reject(new Error('Error de red al subir archivo.')));
          xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
          xhr.open('PUT', presigned.upload_url);
          xhr.setRequestHeader('Content-Type', file.type);
          xhr.send(file);
        });

        setProgress(100);
        setState('uploaded');
        onChange({
          url: presigned.download_url,
          name: file.name,
          size: file.size,
          type: file.type,
        });
      } catch (err: unknown) {
        let msg = 'Error al subir archivo';
        if (err instanceof Error) msg = err.message;
        setErrorMsg(msg);
        setState('error');
      }
    },
    [folder, onChange, presignedMut, validateFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled || state === 'uploading') return;
      const file = e.dataTransfer.files[0];
      if (file) uploadFile(file);
    },
    [disabled, state, uploadFile],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) uploadFile(file);
      if (inputRef.current) inputRef.current.value = '';
    },
    [uploadFile],
  );

  const handleRemove = useCallback(() => {
    setState('idle');
    setErrorMsg('');
    setProgress(0);
    onChange(null);
  }, [onChange]);

  return {
    state,
    errorMsg,
    progress,
    dragOver,
    setDragOver,
    inputRef,
    handleDrop,
    handleFileSelect,
    handleRemove,
  };
}
